import React from 'react';
import { Typography, Box } from '@mui/material';
import { styled } from '@mui/system';
import CCOContainer from '../../fundamentals/CCOContainer';
import PreviewElementBoxDrawer from '../../preview/PreviewElementBoxDrawer';
import { metaDataProperty } from '../../preview/previewMetaData';
import { usePreviewContext } from '../../../context/PreviewContext';
import useWindowSize from '../../../utility/useWindowSize';
import CCOResponsivePicture from '../../fundamentals/CCOResponsivePicture';
import { C014StageCarouselPictureSettings } from '../../../common/constants/pictureSettings';
import { CmPicture } from '../../../../packages/graphql-layer/src';
import { getAltText } from '../../fundamentals/CCOPicture';

export interface Props {
    item?: Item;
}

interface Item {
    id: string;
    picture?: CmPicture | null;
    teaserSettings?: {
        activateH1?: boolean;
    };
    teaserTitle?: string | null;
}

const HeroImage = styled('div')({
    alignItems: 'end',
    display: 'flex',
    justifyContent: 'center',
    overflow: 'hidden',
    position: 'relative',
    textAlign: 'left',
});

const ImageOverlay = styled('div')({
    position: 'absolute',
});

const C082CenteredStageTeaser: React.FC<Props> = ({item}) => {
    const { isPreview } = usePreviewContext();
    const { isWindowExtraSmall, isWindowSmall, isWindowMedium, isWindowLarge, isLoadingBreakpoints } = useWindowSize();

    if (isLoadingBreakpoints || !item || !item.picture) return null;

    const getHeroImageHeight = () => {
        if (isWindowExtraSmall) return '330px';
        if (isWindowSmall) return '338px';
        if (isWindowMedium) return '386px';
        if (isWindowLarge) return '514px';
        return '540px';
    };

    const getMarginBottom = () => {
        if (isWindowExtraSmall || isWindowSmall) return '28px';
        if (isWindowMedium) return '32px';
        if (isWindowLarge) return '44px';
        return '52px';
    };

    const heroImageHeight = getHeroImageHeight();
    const marginBottom = getMarginBottom();

    const textStyle = {
        '& span': {
            background: 'white',
            boxDecorationBreak: 'clone',
            fontSize: {lg: '2.25rem', xl: '2.75rem'},
            lineHeight: {xs: '2.1rem', md: '2.7rem', xl: '3.5rem'},
            marginY: 0,
            paddingX: '16px',
            paddingY: '5px',
        },
        margin: 0,
        padding: 0,
    };

    const truncatedTeaserTitle = item.teaserTitle && item.teaserTitle.length > 150
        ? `${item.teaserTitle.slice(0, 150)}...`
        : item.teaserTitle || '';

    return (
        <PreviewElementBoxDrawer propertyId={item.id}>
            <HeroImage style={{ height: heroImageHeight }}>
                <CCOResponsivePicture
                    alt={getAltText(item.picture.alt, item.picture.title)}
                    hasPriority
                    imageStyle={{ height: '100%', objectFit: 'cover', width: '100%' }}
                    picture={item.picture}
                    pictureSetting={C014StageCarouselPictureSettings}
                    pictureStyle={{ height: '100%', position: 'absolute', width: '100%' }}
                />
                <ImageOverlay />
                <Box sx={{ marginBottom, width: {xs: '100%', lg: '928px'}, zIndex: '0' }}>
                    <CCOContainer width='full'>
                        <Box sx={{ display: 'flex', justifyContent: 'center', marginY: '0', marginX: {xs: '24px', lg: 0} }}>
                            <Typography
                                variant='h3'
                                component={item?.teaserSettings?.activateH1 ? 'h1' : 'div'}
                                sx={textStyle}
                            >
                                <span {...metaDataProperty(isPreview, 'properties.teaserTitle', item.id)}>
                                    {truncatedTeaserTitle}
                                </span>
                            </Typography>
                        </Box>
                    </CCOContainer>
                </Box>
            </HeroImage>
        </PreviewElementBoxDrawer>
    );
};

export default C082CenteredStageTeaser;
